import {
  Link,
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
  useNavigate,
} from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'
import { auth, type AuthState } from '@/lib/auth'
import { useAuth } from '@/lib/useAuth'
import { Button } from '@/components/ui'
import { LoginPage } from '@/pages/LoginPage'
import { TextsPage } from '@/pages/TextsPage'

/**
 * Strom cest skladany rucne (ne codegenem z @tanstack/router-plugin — proc,
 * viz komentar ve vite.config.ts).
 *
 * Kontext routeru drzi stav prihlaseni, takze se na nej da ptat uz
 * v `beforeLoad`, tedy driv, nez se cokoli vykresli.
 */
export interface RouterContext {
  auth: AuthState
  queryClient: QueryClient
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
  notFoundComponent: () => (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <p className="text-4xl font-bold text-slate-300">404</p>
        <p className="mt-2 text-sm text-slate-500">Tahle stránka neexistuje.</p>
      </div>
    </div>
  ),
})

// ── / — jen rozcestnik ───────────────────────────────────────────────────
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: ({ context }) => {
    throw redirect({ to: context.auth.isAuthenticated ? '/texts' : '/login' })
  },
})

// ── /login — neprihlaseni ────────────────────────────────────────────────
function AuthLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold text-brand-700">langs</h1>
          <p className="mt-1 text-sm text-slate-500">Učení jazyků nad vlastním textem</p>
        </div>
        {children}
      </div>
    </div>
  )
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  /**
   * `redirect` v query drzi cestu, na kterou uzivatel puvodne miril, nez ho
   * chraneny layout poslal sem. Validace je zaroven typovanim.
   */
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
  beforeLoad: ({ context }) => {
    // Prihlaseneho uz nema smysl poustet na prihlasovaci formular.
    if (context.auth.isAuthenticated) throw redirect({ to: '/texts' })
  },
  component: function LoginRoute() {
    const { redirect: target } = loginRoute.useSearch()
    return (
      <AuthLayout>
        <LoginPage redirect={target} />
      </AuthLayout>
    )
  },
})

// ── chranena cast ────────────────────────────────────────────────────────
function AppLayout({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3">
          <Link to="/texts" className="text-lg font-bold text-brand-700">
            langs
          </Link>
          <nav className="flex items-center gap-1">
            <Link
              to="/texts"
              className="rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100"
              activeProps={{ className: 'rounded-lg px-3 py-2 text-sm font-medium bg-brand-600 text-white' }}
            >
              Texty
            </Link>
          </nav>
          <div className="ml-auto flex items-center gap-3">
            <span className="hidden text-sm text-slate-500 sm:inline">{user?.displayName ?? user?.email}</span>
            <Button
              variant="ghost"
              onClick={() => {
                auth.logout()
                navigate({ to: '/login' })
              }}
            >
              Odhlásit
            </Button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-4 py-6">{children}</main>
    </div>
  )
}

/**
 * Branka do prihlasene casti. Backend si autorizaci hlida sam — tohle je jen
 * proto, aby uzivatel nekoukal na prazdnou obrazovku s 401.
 */
const requireAuth = ({ context, location }: { context: RouterContext; location: { href: string } }) => {
  if (!context.auth.isAuthenticated) {
    throw redirect({ to: '/login', search: { redirect: location.href } })
  }
}

const textsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/texts',
  beforeLoad: requireAuth,
  component: () => (
    <AppLayout>
      <TextsPage />
    </AppLayout>
  ),
})

const routeTree = rootRoute.addChildren([indexRoute, loginRoute, textsRoute])

export function buildRouter(queryClient: QueryClient) {
  return createRouter({ routeTree, context: { auth, queryClient }, defaultPreload: 'intent' })
}

// Registrace typu routeru — diky ni jsou <Link to="..."> a useSearch()
// typove kontrolovane proti skutecnemu stromu cest.
declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof buildRouter>
  }
}
